/* milsymbol wrapper.
 *
 * DESIGN-SYSTEM.md §7: milsymbol is CONFIGURED, not restyled. Its size/stroke/colour
 * options carry our tokens; the 2525 symbol geometry itself stays standards-exact.
 * Overriding the geometry to look "nicer" would defeat the point of using a standard.
 */
import ms from 'milsymbol'

export interface SymbolOptions {
  size?: number
  /** Contacts render with a thinner stroke than own units (UI-UX-DESIGN.md §4.2). */
  contact?: boolean
  selected?: boolean
  /** 0-1; stale contacts fade in place rather than being hidden. */
  opacity?: number
  standard?: '2525E' | 'APP6E'
  /** Uncertain classification gets the `?` modifier. */
  uncertain?: boolean
}

const cache = new Map<string, string>()

export function symbolSvg(sidc: string, opts: SymbolOptions = {}): string {
  const {
    size = 28,
    contact = false,
    selected = false,
    opacity = 1,
    standard = '2525E',
    uncertain = false,
  } = opts

  const key = `${sidc}|${size}|${contact}|${selected}|${opacity.toFixed(2)}|${standard}|${uncertain}`
  const hit = cache.get(key)
  if (hit) return hit

  const symbol = new ms.Symbol(sidc, {
    size: selected ? size * 1.2 : size,
    standard: standard === 'APP6E' ? 'APP6' : '2525',
    strokeWidth: contact ? 2.5 : 3.5,
    fillOpacity: contact ? 0.16 : 0.34,
    infoColor: '#f1f5f9',
    infoSize: 26,
    uniqueDesignation: undefined,
    ...(uncertain ? { reinforcedReduced: '' } : {}),
  })

  let svg = symbol.asSVG()

  // Selected: accent outline via a drop-shadow filter, applied to the wrapper not the geometry.
  if (selected) {
    svg = svg.replace(
      '<svg',
      '<svg style="filter:drop-shadow(0 0 3px var(--color-accent)) drop-shadow(0 0 8px rgba(59,130,246,.55))"',
    )
  }
  if (opacity < 1) {
    svg = svg.replace('<svg', `<svg opacity="${opacity.toFixed(2)}"`)
  }

  cache.set(key, svg)
  return svg
}

/** Anchor offsets so the symbol's centre sits on its geographic position. */
export function symbolAnchor(sidc: string, size = 28): { w: number; h: number } {
  const s = new ms.Symbol(sidc, { size })
  const b = s.getSize()
  return { w: b.width, h: b.height }
}

/** Staleness → opacity. A track that keeps gliding after contact is lost is a lie. */
export function stalenessOpacity(ageSec: number): number {
  if (ageSec < 15) return 1
  if (ageSec < 90) return 0.78
  if (ageSec < 300) return 0.52
  return 0.34
}
